
import React from 'react';
import { McAccordion, McAccordionItem, McNotification } from '@maersk-global/mds-react-wrapper/components-core';

export default function FAQPage() {
    return (
        <>
            <div style={{ marginBottom: '24px' }}>
                <h1 className="mds-headline--medium-bold" style={{ marginBottom: '8px' }}>
                    Help Center
                </h1>
                <p className="mds-text--medium-regular" style={{ color: 'var(--mds-color-neutral-weaker-text)', margin: 0 }}>
                    Frequently asked questions and guides to help you get the most out of the Self Service Query tool.
                </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <McAccordion>
                    <McAccordionItem>
                        <div slot="heading" style={{ fontWeight: 600 }}>How do I create a new query?</div>
                        <div slot="body" style={{ padding: '1rem' }}>
                            You can create a new query by navigating to the "Queries" page and clicking the "New Query" button.
                            You will be guided through a wizard to select a terminal, choose tables, and write your SQL query.
                            Alternatively, you can use "Smart Create" to generate a query using AI.
                        </div>
                    </McAccordionItem>

                    <McAccordionItem>
                        <div slot="heading" style={{ fontWeight: 600 }}>What is the difference between 'Active' and 'Inactive' queries?</div>
                        <div slot="body" style={{ padding: '1rem' }}>
                            Active queries are available for execution and can be scheduled.
                            Inactive queries are saved as drafts or archived versions and cannot be executed directly until activated.
                        </div>
                    </McAccordionItem>

                    <McAccordionItem>
                        <div slot="heading" style={{ fontWeight: 600 }}>How does scheduling work?</div>
                        <div slot="body" style={{ padding: '1rem' }}>
                            You can schedule a query to run automatically at specific intervals using cron expressions.
                            Navigate to the "Schedules" page to create a new schedule.
                            Scheduled queries execute in the background, and their results are saved for analytics.
                        </div>
                    </McAccordionItem>

                    <McAccordionItem>
                        <div slot="heading" style={{ fontWeight: 600 }}>Can I execute a query on multiple terminals?</div>
                        <div slot="body" style={{ padding: '1rem' }}>
                            Yes, when creating or executing a query, you can select the target terminal.
                            Schedules can also be configured to run the same query across multiple selected terminals.
                        </div>
                    </McAccordionItem>

                    <McAccordionItem>
                        <div slot="heading" style={{ fontWeight: 600 }}>How do I view query execution history?</div>
                        <div slot="body" style={{ padding: '1rem' }}>
                            You can view the execution history of a specific query by going to its details page.
                            Global analytics and history are available on the "Analytics" page.
                        </div>
                    </McAccordionItem>

                    <McAccordionItem>
                        <div slot="heading" style={{ fontWeight: 600 }}>How do scheduled query snapshots work?</div>
                        <div slot="body" style={{ padding: '1rem' }}>
                            When a query is scheduled, the system executes it at the specified intervals. The results are persisted into a specified 'Result Table' in the Data Lake, allowing for historical analysis. Each execution appends a new set of rows with metadata (execution_time, batch_id). You can access this data via the 'Stored Results' view in the UI or programmatically.
                        </div>
                    </McAccordionItem>

                    <McAccordionItem>
                        <div slot="heading" style={{ fontWeight: 600 }}>How can developers access data programmatically?</div>
                        <div slot="body" style={{ padding: '1rem' }}>
                            Developers can retrieve stored snapshot data using the REST API. The endpoint <code>GET /api/v1/results/{"{tableName}"}</code> allows fetching paginated results. You can filter by <code>terminalId</code> to get region-specific data. Alternatively, if you have direct DB access, you can query the result tables using standard SQL.
                        </div>
                    </McAccordionItem>

                    <McAccordionItem>
                        <div slot="heading" style={{ fontWeight: 600 }}>How does the Schema Explorer work?</div>
                        <div slot="body" style={{ padding: '1rem' }}>
                            The Schema Explorer connects to the selected terminal's database using a read-only service account. It queries the system catalog (e.g., ALL_TABLES in Oracle) to retrieve real-time schema information without executing user queries, ensuring safety and performance.
                        </div>
                    </McAccordionItem>

                    <McAccordionItem>
                        <div slot="heading" style={{ fontWeight: 600 }}>What AI capabilities are available?</div>
                        <div slot="body" style={{ padding: '1rem' }}>
                            The application features <strong>Smart Create</strong> and <strong>Optimize SQL</strong> powered by Large Language Models (LLMs). 'Smart Create' translates natural language requests into valid SQL syntax. 'Optimize SQL' analyzes your query plan to suggest performance improvements and index usage.
                        </div>
                    </McAccordionItem>

                    <McAccordionItem>
                        <div slot="heading" style={{ fontWeight: 600 }}>Can I trigger executions via API?</div>
                        <div slot="body" style={{ padding: '1rem' }}>
                            Yes. The Execute Query API (<code>POST /api/v1/queries/{"{id}"}/execute</code>) allows external systems, such as CI/CD pipelines, to trigger query executions on demand. You must provide the valid authentication credentials and target terminal ID.
                        </div>
                    </McAccordionItem>

                    <McAccordionItem>
                        <div slot="heading" style={{ fontWeight: 600 }}>Who can modify, deactivate, or activate a query?</div>
                        <div slot="body" style={{ padding: '1rem' }}>
                            Access is governed by Role-Based Access Control (RBAC). Only users with the <strong>'Editor'</strong> or <strong>'Admin'</strong> role for the specific <em>Application</em> associated with the query can modify its code or change its state (active/inactive). Users with 'Viewer' access can only execute queries.
                        </div>
                    </McAccordionItem>

                    <McAccordionItem>
                        <div slot="heading" style={{ fontWeight: 600 }}>What does 'Application' refer to?</div>
                        <div slot="body" style={{ padding: '1rem' }}>
                            An <strong>Application</strong> represents a specific business domain or system (e.g., 'Sentinel', 'Compass', 'Truck Appointment System','HazDV'). Queries are grouped by Application to ensure proper ownership and security. To onboard a new Application, please use the <strong>'Onboarding request'</strong> button located in the top navigation bar.
                        </div>
                    </McAccordionItem>

                    <McAccordionItem>
                        <div slot="heading" style={{ fontWeight: 600 }}>How do I get access to create new queries?</div>
                        <div slot="body" style={{ padding: '1rem' }}>
                            Access is managed via Identity Management (IDM). You must request the <strong>'Onboarding request'</strong>  for the specific Application domain you wish to work on. Once approved, the 'New Query' button will become enabled for you.
                        </div>
                    </McAccordionItem>

                    <McAccordionItem>
                        <div slot="heading" style={{ fontWeight: 600 }}>Business User: How can I use this system?</div>
                        <div slot="body" style={{ padding: '1rem' }}>
                            Business users can utilize the <strong>Data Catalog</strong> to browse and find approved, curated queries for ad-hoc analysis without needing to write SQL.
                        </div>
                    </McAccordionItem>

                    <McAccordionItem>
                        <div slot="heading" style={{ fontWeight: 600 }}>Tech Support: How do I troubleshoot issues?</div>
                        <div slot="body" style={{ padding: '1rem' }}>
                            Support teams should use the <strong>Execution History</strong> to view detailed error logs and stack traces for failed queries. The <strong>'Optimize SQL'</strong> tool can also be used to generate 'Explain Plans' to diagnose performance bottlenecks in the database.
                        </div>
                    </McAccordionItem>
                </McAccordion>
            </div>

            {/* ════════════════ AI AGENTS FAQ ════════════════ */}
            <div style={{ marginTop: '48px', marginBottom: '24px' }}>
                <h2 className="mds-headline--medium-bold" style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span>🤖</span> AI Agents FAQ
                </h2>
                <p className="mds-text--medium-regular" style={{ color: 'var(--mds-color-neutral-weaker-text)', margin: 0 }}>
                    Everything you need to know about building, configuring, and using AI Agents.
                </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <McAccordion>
                    <McAccordionItem>
                        <div slot="heading" style={{ fontWeight: 600 }}>What are AI Agents?</div>
                        <div slot="body" style={{ padding: '1rem' }}>
                            AI Agents are intelligent, autonomous workflows powered by Large Language Models (LLMs) such as GPT-4o.
                            Unlike simple chatbots, agents can <strong>reason about tasks</strong>, <strong>use tools</strong> (query databases, call APIs, search repositories),
                            and <strong>remember context</strong> across conversations. You build agents visually using the drag-and-drop Agent Builder,
                            and they can be triggered from WhatsApp, Microsoft Teams, scheduled jobs, or REST API calls.
                        </div>
                    </McAccordionItem>

                    <McAccordionItem>
                        <div slot="heading" style={{ fontWeight: 600 }}>How do I create an AI Agent?</div>
                        <div slot="body" style={{ padding: '1rem' }}>
                            <ol style={{ margin: 0, paddingLeft: '1.5rem' }}>
                                <li>Navigate to <strong>AI Agents</strong> from the sidebar menu</li>
                                <li>Click <strong>"New Agent"</strong> to open the Agent Builder</li>
                                <li>Drag a <strong>Trigger</strong> node (WhatsApp, Teams, Manual, or Cron) onto the canvas</li>
                                <li>Drag an <strong>AI Action</strong> node and connect it to the trigger</li>
                                <li>Configure the system prompt, model, and temperature</li>
                                <li>Assign <strong>Tools</strong> (Query, API, GitHub, Memory) to the AI Action</li>
                                <li>Click <strong>Save</strong>, then <strong>Test</strong>, and finally <strong>Deploy</strong></li>
                            </ol>
                            <p style={{ marginTop: '12px', color: '#6B7280' }}>
                                You can also use a <strong>Recipe</strong> (pre-configured template) from the component palette for common use cases.
                            </p>
                        </div>
                    </McAccordionItem>

                    <McAccordionItem>
                        <div slot="heading" style={{ fontWeight: 600 }}>What tools are currently available for agents?</div>
                        <div slot="body" style={{ padding: '1rem' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                <thead>
                                    <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
                                        <th style={{ textAlign: 'left', padding: '8px' }}>Tool</th>
                                        <th style={{ textAlign: 'left', padding: '8px' }}>Status</th>
                                        <th style={{ textAlign: 'left', padding: '8px' }}>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr style={{ borderBottom: '1px solid #F3F4F6' }}>
                                        <td style={{ padding: '8px', fontWeight: 600 }}>🔍 Query Service</td>
                                        <td style={{ padding: '8px' }}><span style={{ color: '#059669', fontWeight: 600 }}>Available</span></td>
                                        <td style={{ padding: '8px' }}>Search/execute saved queries, list terminals, explore schemas</td>
                                    </tr>
                                    <tr style={{ borderBottom: '1px solid #F3F4F6' }}>
                                        <td style={{ padding: '8px', fontWeight: 600 }}>🌐 REST API</td>
                                        <td style={{ padding: '8px' }}><span style={{ color: '#059669', fontWeight: 600 }}>Available</span></td>
                                        <td style={{ padding: '8px' }}>Call any HTTP endpoint (GET, POST, PUT, DELETE, PATCH)</td>
                                    </tr>
                                    <tr style={{ borderBottom: '1px solid #F3F4F6' }}>
                                        <td style={{ padding: '8px', fontWeight: 600 }}>🐙 GitHub</td>
                                        <td style={{ padding: '8px' }}><span style={{ color: '#059669', fontWeight: 600 }}>Available</span></td>
                                        <td style={{ padding: '8px' }}>Search repos, read files, create PRs, list issues</td>
                                    </tr>
                                    <tr style={{ borderBottom: '1px solid #F3F4F6' }}>
                                        <td style={{ padding: '8px', fontWeight: 600 }}>🧠 Memory</td>
                                        <td style={{ padding: '8px' }}><span style={{ color: '#059669', fontWeight: 600 }}>Available</span></td>
                                        <td style={{ padding: '8px' }}>Persistent key-value memory (in-memory or Postgres-backed)</td>
                                    </tr>
                                    <tr style={{ borderBottom: '1px solid #F3F4F6' }}>
                                        <td style={{ padding: '8px', fontWeight: 600 }}>📁 SharePoint</td>
                                        <td style={{ padding: '8px' }}><span style={{ color: '#2563EB', fontWeight: 600 }}>Beta</span></td>
                                        <td style={{ padding: '8px' }}>Search documents, list sites, read files from SharePoint Online</td>
                                    </tr>
                                    <tr style={{ borderBottom: '1px solid #F3F4F6' }}>
                                        <td style={{ padding: '8px', fontWeight: 600 }}>📖 Confluence</td>
                                        <td style={{ padding: '8px' }}><span style={{ color: '#2563EB', fontWeight: 600 }}>Beta</span></td>
                                        <td style={{ padding: '8px' }}>Search pages, read content, list spaces via Atlassian Cloud API</td>
                                    </tr>
                                    <tr style={{ borderBottom: '1px solid #F3F4F6' }}>
                                        <td style={{ padding: '8px', fontWeight: 600 }}>🎫 Jira</td>
                                        <td style={{ padding: '8px' }}><span style={{ color: '#2563EB', fontWeight: 600 }}>Beta</span></td>
                                        <td style={{ padding: '8px' }}>Search/create issues, add comments, list projects via JQL</td>
                                    </tr>
                                    <tr style={{ borderBottom: '1px solid #F3F4F6' }}>
                                        <td style={{ padding: '8px', fontWeight: 600 }}>📧 Outlook Mail</td>
                                        <td style={{ padding: '8px' }}><span style={{ color: '#2563EB', fontWeight: 600 }}>Beta</span></td>
                                        <td style={{ padding: '8px' }}>Search, read, and send emails via Microsoft Graph API</td>
                                    </tr>
                                    <tr style={{ borderBottom: '1px solid #F3F4F6' }}>
                                        <td style={{ padding: '8px', fontWeight: 600 }}>📅 Outlook Calendar</td>
                                        <td style={{ padding: '8px' }}><span style={{ color: '#2563EB', fontWeight: 600 }}>Beta</span></td>
                                        <td style={{ padding: '8px' }}>List events, create/cancel meetings via Microsoft Graph API</td>
                                    </tr>
                                    <tr>
                                        <td style={{ padding: '8px', fontWeight: 600 }}>💬 Teams</td>
                                        <td style={{ padding: '8px' }}><span style={{ color: '#2563EB', fontWeight: 600 }}>Beta</span></td>
                                        <td style={{ padding: '8px' }}>List teams/channels, send messages, direct chat via Graph API</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </McAccordionItem>

                    <McAccordionItem>
                        <div slot="heading" style={{ fontWeight: 600 }}>How does the Memory tool work?</div>
                        <div slot="body" style={{ padding: '1rem' }}>
                            <p>The Memory tool gives agents the ability to <strong>remember information</strong> across conversations. It supports 5 operations:</p>
                            <ul>
                                <li><strong>memorySave</strong> — store a key-value pair</li>
                                <li><strong>memoryGet</strong> — retrieve a value by key</li>
                                <li><strong>memoryList</strong> — list all stored memories</li>
                                <li><strong>memoryDelete</strong> — remove a specific key</li>
                                <li><strong>memorySearch</strong> — search across keys and values by keyword</li>
                            </ul>
                            <p style={{ marginTop: '12px' }}><strong>Storage Modes:</strong></p>
                            <ul>
                                <li><strong>In-Memory (POD)</strong> — fast, ephemeral storage that resets when the pod restarts. Good for session-level context.</li>
                                <li><strong>Postgres</strong> — persistent storage backed by the <code>agent_memory</code> table. Survives restarts and is scoped per agent + user.</li>
                            </ul>
                            <p style={{ color: '#6B7280', marginTop: '8px' }}>Configure the storage type in the Agent Builder by selecting the Memory tool and choosing the "Storage Type" dropdown.</p>
                        </div>
                    </McAccordionItem>

                    <McAccordionItem>
                        <div slot="heading" style={{ fontWeight: 600 }}>How do I connect my agent to WhatsApp or Teams?</div>
                        <div slot="body" style={{ padding: '1rem' }}>
                            <p>When building your agent, drag a <strong>Trigger</strong> node and choose the channel:</p>
                            <ul>
                                <li><strong>WhatsApp</strong> — uses the WhatsApp Business API. Messages from users are routed to your agent. The agent's response is sent back via the same channel.</li>
                                <li><strong>Microsoft Teams</strong> — uses a Teams bot connector. The agent receives messages from the bot and replies in the chat thread.</li>
                            </ul>
                            <p style={{ color: '#6B7280', marginTop: '8px' }}>Once deployed, the agent automatically listens on the configured channel. No additional webhook setup is needed.</p>
                        </div>
                    </McAccordionItem>

                    <McAccordionItem>
                        <div slot="heading" style={{ fontWeight: 600 }}>Can I schedule an agent to run automatically?</div>
                        <div slot="body" style={{ padding: '1rem' }}>
                            Yes! Add a <strong>Cron Trigger</strong> node to your agent workflow. Configure it with a cron expression
                            (e.g., <code>0 8 * * 1-5</code> for every weekday at 8 AM). The agent will execute at the specified times
                            and can send results to a notification channel, email, or store them using the Memory tool.
                        </div>
                    </McAccordionItem>

                    <McAccordionItem>
                        <div slot="heading" style={{ fontWeight: 600 }}>What models are supported?</div>
                        <div slot="body" style={{ padding: '1rem' }}>
                            Currently supported models:
                            <ul>
                                <li><strong>GPT-4o</strong> — most capable, best for complex reasoning and multi-tool usage</li>
                                <li><strong>GPT-4o Mini</strong> — faster and cheaper, suitable for simpler tasks</li>
                                <li><strong>GPT-3.5 Turbo</strong> — fastest and most cost-effective for straightforward Q&A</li>
                            </ul>
                            <p style={{ color: '#6B7280', marginTop: '8px' }}>Select the model in the AI Action node's configuration panel. You can also adjust the temperature (0.0 = deterministic, 1.0 = creative).</p>
                        </div>
                    </McAccordionItem>

                    <McAccordionItem>
                        <div slot="heading" style={{ fontWeight: 600 }}>How do I add a new tool as a developer?</div>
                        <div slot="body" style={{ padding: '1rem' }}>
                            <p>The tool system is designed to be <strong>fully extensible</strong>. To add a new tool (e.g. SharePoint):</p>
                            <ol style={{ paddingLeft: '1.5rem' }}>
                                <li><strong>Frontend:</strong> Add an entry to the <code>AVAILABLE_TOOLS</code> array in <code>AddToolDialog.tsx</code> with <code>configFields</code>. The UI renders config fields automatically.</li>
                                <li><strong>Backend:</strong> Create a new <code>@Tool</code> class (e.g., <code>SharePointTool.java</code>) following the ThreadLocal context pattern.</li>
                                <li><strong>Wire it:</strong> Inject the bean in <code>AgentExecutionService.java</code> and add it to the tools list when enabled.</li>
                            </ol>
                            <p style={{ color: '#6B7280', marginTop: '8px' }}>See the Developer Docs → AI Agents Guide for detailed code samples and architecture diagrams.</p>
                        </div>
                    </McAccordionItem>

                    <McAccordionItem>
                        <div slot="heading" style={{ fontWeight: 600 }}>My agent execution failed — how do I troubleshoot?</div>
                        <div slot="body" style={{ padding: '1rem' }}>
                            <ol style={{ paddingLeft: '1.5rem' }}>
                                <li>Check the <strong>Activity Logs</strong> tab on the agent's page for error details and stack traces.</li>
                                <li>Verify that all tool configurations are correct (e.g., GitHub token, Memory storage type).</li>
                                <li>Ensure the agent has the proper <strong>system prompt</strong> — vague prompts cause the LLM to misuse tools.</li>
                                <li>If you see <code>TokenLimitExceeded</code>, reduce the conversation history or switch to a model with a larger context window.</li>
                                <li>For persistent issues, check the agent-service logs: <code>kubectl logs -l app=agent-service --tail=200</code></li>
                            </ol>
                        </div>
                    </McAccordionItem>

                    <McAccordionItem>
                        <div slot="heading" style={{ fontWeight: 600 }}>Can I execute an agent via REST API?</div>
                        <div slot="body" style={{ padding: '1rem' }}>
                            <p>Yes. Use the agent execution API:</p>
                            <div style={{ fontFamily: 'monospace', fontSize: '12px', backgroundColor: '#F3F4F6', padding: '12px', borderRadius: '6px', overflowX: 'auto' }}>
                                <pre style={{ margin: 0 }}>{`POST /apmt-cdh-agent-service/agents/{agentId}/execute
Content-Type: application/json
Authorization: Bearer <token>

{
  "message": "Your question here",
  "channel": "web_ui",
  "userId": "user@maersk.com"
}`}</pre>
                            </div>
                            <p style={{ color: '#6B7280', marginTop: '8px' }}>This is useful for integrating agents into CI/CD pipelines, external dashboards, or custom applications.</p>
                        </div>
                    </McAccordionItem>
                </McAccordion>
            </div>

        </>
    );
}
